import { DiscountCodeTypes } from "../../enum";
import { calculateProductTaxBreakdown, getProductTaxesPercentage } from "./helpers";
import { resolveBillableQuantity } from "./billableQuantity";
import { isNoChargeReturnLine } from "@washday/utilities";
const getNormalizedId = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
};
const getProductIdentity = (product) => {
    var _a;
    const directId = getNormalizedId((_a = product === null || product === void 0 ? void 0 : product._id) !== null && _a !== void 0 ? _a : product === null || product === void 0 ? void 0 : product.productId);
    if (directId) {
        return directId;
    }
    const storeProductId = product === null || product === void 0 ? void 0 : product.storeProductId;
    if (typeof storeProductId === "string") {
        return getNormalizedId(storeProductId);
    }
    return getNormalizedId(storeProductId === null || storeProductId === void 0 ? void 0 : storeProductId._id);
};
const getDiscountTargetIdentity = (target) => {
    var _a, _b;
    if (typeof target === "string") {
        return getNormalizedId(target);
    }
    return getNormalizedId((_b = (_a = target === null || target === void 0 ? void 0 : target._id) !== null && _a !== void 0 ? _a : target === null || target === void 0 ? void 0 : target.productId) !== null && _b !== void 0 ? _b : target === null || target === void 0 ? void 0 : target.storeProductId);
};
const hasMatchingDiscountTarget = (targets = [], currentProduct) => {
    const currentId = getProductIdentity(currentProduct);
    if (!currentId) {
        return false;
    }
    return (Array.isArray(targets) ? targets : []).some((target) => {
        return getDiscountTargetIdentity(target) === currentId;
    });
};
export const calculateTotalTaxesIncluded = (order, selectedCustomer, storeSettings, storeDiscounts, appliedOrderDiscounts, discountCodeObj) => {
    const productTableImports = [...order.products, ...order.buyAndGetProducts].map((current) => {
        var _a, _b, _c;
        const actualQuantity = Number((_b = (_a = current.qty) !== null && _a !== void 0 ? _a : current.quantity) !== null && _b !== void 0 ? _b : 0);
        if (isNoChargeReturnLine(order, current)) {
            return {
                product: current,
                qty: actualQuantity,
                productLineImportTotal: 0,
                productLineTotalWithDiscount: 0,
                productLineTaxesTotal: 0,
                taxBreakdown: [],
                lineDiscountAmount: 0,
                productLineSubtotal: 0,
                lineNetAfterDiscount: 0,
            };
        }
        const billableQuantity = resolveBillableQuantity(current);
        const taxPercentage = getProductTaxesPercentage(current, storeSettings);
        const taxFactor = 1 + taxPercentage / 100;
        // Convertir extraAmount de bruto a neto
        const extraAmountGross = current.extraAmount || 0;
        const extraAmountNet = extraAmountGross / taxFactor;
        // Precio bruto del producto (sin extraAmount)
        const productGrossPrice = order.express ? current.expressPrice : current.price;
        // Convertir precio bruto a neto
        const unitNetPrice = productGrossPrice / taxFactor;
        // Determinar el porcentaje de descuento (si aplica)
        let discPercentageInteger = 0;
        let productPercentageDiscount = 0;
        let customerDiscount = 0;
        // ORDER DOES NOT HAVE A DISCOUNT CODE
        if (!order.discountCode) {
            const discountsToApply = storeDiscounts.filter((discount) => discount.isActive && hasMatchingDiscountTarget(discount.products, current));
            customerDiscount = ((_c = selectedCustomer === null || selectedCustomer === void 0 ? void 0 : selectedCustomer.customer) === null || _c === void 0 ? void 0 : _c.discount) || 0;
            productPercentageDiscount = discountsToApply.reduce((prev, next) => {
                return next.type === 'percentage' ? prev + next.value : prev;
            }, 0);
            discPercentageInteger = +((productPercentageDiscount + customerDiscount) / 100).toFixed(2);
            discountsToApply.forEach((discount) => (appliedOrderDiscounts[discount._id] = discount));
        }
        else {
            // ORDER HAS A DISCOUNT CODE
            // PERCENTAGE DISCOUNT CODE
            if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.PERCENTAGE) {
                discPercentageInteger = +(discountCodeObj.value / 100).toFixed(2);
                if (!discountCodeObj.applyToAllProducts) {
                    discPercentageInteger = hasMatchingDiscountTarget(discountCodeObj.products, current)
                        ? +(discountCodeObj.value / 100).toFixed(2)
                        : 0;
                }
            }
            if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.BUY_X_GET_Y) {
                const discountType = discountCodeObj.buyAndGetConditions[0].getDiscountType;
                const discountValue = discountCodeObj.buyAndGetConditions[0].discountValue;
                if (discountType === 'percentage' && current.isBuyAndGetProduct) {
                    discPercentageInteger = +(discountValue / 100).toFixed(2);
                }
                if (discountType === 'free' && current.isBuyAndGetProduct) {
                    discPercentageInteger = 1;
                }
                // FREE ITEM DISCOUNT CODE
            }
            else if ((discountCodeObj === null || discountCodeObj === void 0 ? void 0 : discountCodeObj.type) === DiscountCodeTypes.FREE_ITEM && (current.isFreeItem)) {
                discPercentageInteger = 1;
            }
        }
        // === NUEVO: Aplicar descuento sobre el precio total ajustado (producto base + extra prorrateado) ===
        // Si hay cantidad, repartir el extra neto entre cada unidad
        const unitExtraNet = billableQuantity > 0 ? extraAmountNet / billableQuantity : 0;
        // Precio neto ajustado: precio base + extra (por unidad)
        const adjustedUnitNetPrice = unitNetPrice + unitExtraNet;
        // Calcular el descuento por unidad.
        // - PERCENTAGE/BUY_X_GET_Y/FREE_ITEM: derive from `discPercentageInteger`
        //   (already applied as percentage on adjusted net unit price).
        // - NUMBER (fixed): `current.discountAmount` is the per-line GROSS
        //   discount in pesos, baked by `applyDiscountToProducts`. Convert to
        //   per-unit net so the existing pipeline computes tax post-discount.
        let discountAmountPerUnit = discPercentageInteger ? adjustedUnitNetPrice * discPercentageInteger : 0;
        const isFixedNumberCoupon = !!discountCodeObj &&
            discountCodeObj.type === DiscountCodeTypes.NUMBER &&
            Number(current.discountAmount) > 0;
        if (isFixedNumberCoupon && billableQuantity > 0) {
            const perUnitGrossDiscount = Number(current.discountAmount) / billableQuantity;
            const perUnitNetDiscount = perUnitGrossDiscount / taxFactor;
            discountAmountPerUnit = Math.min(adjustedUnitNetPrice, perUnitNetDiscount);
        }
        // Precio neto con descuento aplicado por unidad
        const discountedUnitNetPrice = Math.max(0, adjustedUnitNetPrice - discountAmountPerUnit);
        // === Totales de la línea ===
        // Total neto sin descuento: precio base total + extra (ya prorrateado en el total)
        const productNetTotalWithoutDiscount = (unitNetPrice * billableQuantity) + extraAmountNet;
        // Total neto con descuento: se aplica el descuento sobre el precio ajustado por unidad
        const productNetTotalWithDiscount = discountedUnitNetPrice * billableQuantity;
        // Reaplicar IVA a ambos totales
        const productLineImportTotal = +(productNetTotalWithoutDiscount * taxFactor).toFixed(2);
        const productLineTotalWithDiscount = +(productNetTotalWithDiscount * taxFactor).toFixed(2);
        // Calcular el total de impuestos como la diferencia entre bruto y neto con descuento
        const totalTaxesApplied = +(productLineTotalWithDiscount - productNetTotalWithDiscount).toFixed(2);
        const taxBreakdown = calculateProductTaxBreakdown({
            product: current,
            store: storeSettings,
            lineTaxAmount: totalTaxesApplied,
        });
        // For NUMBER coupons the user-facing "Descuento" is the gross slice
        // (which is what was baked into `current.discountAmount`). For other
        // discount types, line discount = per-unit net × qty (existing).
        const lineDiscount = isFixedNumberCoupon
            ? Number(current.discountAmount)
            : +(discountAmountPerUnit * billableQuantity).toFixed(2);
        return {
            product: current,
            qty: actualQuantity,
            productLineImportTotal, // Total bruto sin descuento (incluye extra y IVA)
            productLineTotalWithDiscount, // Total bruto con descuento aplicado sobre el precio ajustado
            productLineTaxesTotal: totalTaxesApplied,
            taxBreakdown,
            lineDiscountAmount: lineDiscount,
            productLineSubtotal: productNetTotalWithoutDiscount, // Subtotal neto (sin IVA) — for included this is NET before discount
            lineNetAfterDiscount: productNetTotalWithDiscount, // NET after discount — needed to derive pricingBreakdown
        };
    });
    return productTableImports;
};
